import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useState, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { toast } from "sonner";
import { ArrowLeft, Code, FileText, RefreshCw } from "lucide-react";
import { generateMetaHtml, parseMetaHtml } from "@/lib/metadata-utils";

interface PageMetadataFormProps {
  initialData?: any;
  pagePath?: string;
  onBack: () => void;
  onSave: () => void;
}

export default function PageMetadataForm({ initialData, pagePath: initialPagePath, onBack, onSave }: PageMetadataFormProps) {
  const [pagePath, setPagePath] = useState(initialPagePath || "");
  const [isRawMode, setIsRawMode] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    keywords: "",
    robots: "index, follow",
    canonicalPath: "", 
    author: "Benvora Bioscience Team",
    copyright: "Benvora Bioscience 2025",
    ogTitle: "",
    ogDescription: "",
    ogImage: "",
    ogUrl: "",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "",
    twitterDescription: "",
    twitterImage: "",
    favicon: "",
    hreflang: "en-US",
    geoRegion: "IN-HR",
    geoPlacename: "Panchkula, Haryana",
    geoPosition: "30.6942;76.8606",
    customMetaHtml: ""
  });

  const saveMetadata = useMutation((api as any).pageMetadata.savePageMetadata);

  useEffect(() => {
    if (initialData) {
      let canonicalPath = "";
      if (initialData.canonical && initialData.canonical.startsWith("https://www.benvorabioscience.com/")) {
        canonicalPath = initialData.canonical.replace("https://www.benvorabioscience.com/", "");
      } else if (initialData.canonical) {
        canonicalPath = initialData.canonical;
      }

      setFormData({
        ...initialData,
        canonicalPath: canonicalPath,
        customMetaHtml: initialData.customMetaHtml || ""
      });
      setIsRawMode(initialData.isRawMode || false);
    }
    if (initialPagePath) {
      setPagePath(initialPagePath);
    }
  }, [initialData, initialPagePath]);

  const handleModeToggle = (checked: boolean) => {
    setIsRawMode(checked);
    if (checked) {
      // Switching to HTML mode: Generate HTML from fields
      const html = generateMetaHtml({
        ...formData,
        canonical: formData.canonicalPath
      });
      setFormData(prev => ({ ...prev, customMetaHtml: html }));
      toast.info("Generated HTML from fields");
    } else {
      // Switching to Form mode: Parse HTML to fields
      const parsed = parseMetaHtml(formData.customMetaHtml);
      setFormData(prev => ({
        ...prev,
        ...parsed,
        canonicalPath: parsed.canonical || prev.canonicalPath
      }));
      toast.info("Updated fields from HTML");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const fullCanonical = `https://www.benvorabioscience.com/${formData.canonicalPath}`.replace(/([^:]\/)\/+/g, "$1");

      const { canonicalPath, ...rest } = formData;
      
      await saveMetadata({
        pagePath: pagePath,
        ...rest,
        canonical: fullCanonical,
        isRawMode
      });
      toast.success("Page metadata saved successfully");
      onSave();
    } catch (error) {
      console.error(error);
      toast.error("Failed to save metadata");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-2xl font-bold">{initialData ? "Edit Page Metadata" : "Add New Page Metadata"}</h2>
        </div>
        <div className="flex items-center gap-2 bg-muted p-2 rounded-lg">
          <Switch 
            checked={isRawMode} 
            onCheckedChange={handleModeToggle} 
            id="raw-mode"
          />
          <Label htmlFor="raw-mode" className="cursor-pointer flex items-center gap-2">
            {isRawMode ? <Code className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
            {isRawMode ? "HTML Mode" : "Form Mode"}
          </Label>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="space-y-4 border-b pb-6">
          <h3 className="text-lg font-semibold">Page Configuration</h3>
          <div className="space-y-2">
            <Label htmlFor="pagePath">Page Path (e.g., /about)</Label>
            <Input
              id="pagePath"
              value={pagePath}
              onChange={(e) => setPagePath(e.target.value)}
              placeholder="/your-page-path"
              disabled={!!initialData}
            />
          </div>
        </div>

        {isRawMode ? (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>HTML Mode:</strong> Edit the raw HTML metadata below. Switching back to Form Mode will parse these tags back into the fields.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="customMetaHtml">Raw HTML Metadata</Label>
              <Textarea
                id="customMetaHtml"
                value={formData.customMetaHtml}
                onChange={(e) => setFormData({ ...formData, customMetaHtml: e.target.value })}
                placeholder="<title>My Page</title>&#10;<meta name='description' content='...' />"
                rows={20}
                className="font-mono text-sm"
              />
            </div>
          </div>
        ) : (
          <>
            {/* Basic SEO Section */}
            <div className="space-y-4 border-b pb-6">
              <h3 className="text-lg font-semibold">Basic SEO</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Page Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter page title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="canonical">Canonical URL Suffix</Label>
                  <div className="flex items-center">
                    <span className="bg-muted px-3 py-2 border border-r-0 rounded-l-md text-sm text-muted-foreground whitespace-nowrap">
                      https://www.benvorabioscience.com/
                    </span>
                    <Input
                      id="canonical"
                      value={formData.canonicalPath}
                      onChange={(e) => setFormData({ ...formData, canonicalPath: e.target.value })}
                      placeholder="page-path"
                      className="rounded-l-none"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Meta Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter meta description"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="keywords">Keywords</Label>
                <Input
                  id="keywords"
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  placeholder="keyword1, keyword2, keyword3"
                />
              </div>
            </div>

            {/* Social Media Section */}
            <div className="space-y-4 border-b pb-6">
              <h3 className="text-lg font-semibold">Social Media (Open Graph & Twitter)</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ogTitle">Open Graph Title</Label>
                  <Input
                    id="ogTitle"
                    value={formData.ogTitle}
                    onChange={(e) => setFormData({ ...formData, ogTitle: e.target.value })}
                    placeholder="Title for social media"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="twitterTitle">Twitter Title</Label>
                  <Input
                    id="twitterTitle"
                    value={formData.twitterTitle}
                    onChange={(e) => setFormData({ ...formData, twitterTitle: e.target.value })}
                    placeholder="Title for Twitter"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ogDescription">Open Graph Description</Label>
                <Textarea
                  id="ogDescription"
                  value={formData.ogDescription}
                  onChange={(e) => setFormData({ ...formData, ogDescription: e.target.value })}
                  placeholder="Description for social media"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ogImage">Open Graph Image URL</Label>
                <Input
                  id="ogImage"
                  value={formData.ogImage}
                  onChange={(e) => setFormData({ ...formData, ogImage: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>

            {/* Advanced Section */}
            <div className="space-y-4 border-b pb-6">
              <h3 className="text-lg font-semibold">Advanced Settings</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="robots">Robots Meta Tag</Label>
                  <Input
                    id="robots"
                    value={formData.robots}
                    onChange={(e) => setFormData({ ...formData, robots: e.target.value })}
                    placeholder="index, follow"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="author">Author</Label>
                  <Input
                    id="author"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    placeholder="Author name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="geoRegion">Geo Region</Label>
                  <Input
                    id="geoRegion"
                    value={formData.geoRegion}
                    onChange={(e) => setFormData({ ...formData, geoRegion: e.target.value })}
                    placeholder="IN-HR"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        <Button type="submit" className="w-full" size="lg">
          Save Metadata
        </Button>
      </form>
    </div>
  );
}